import { IAssignmentTask, userSharedSelectors } from 'common/modules/ReactShared';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { assignmentTaskAdapter } from './adapters';
import { prepareChatMessagesForTaskAssignment } from './prepareChatMessages';
import { tAssignmentNormalizedData, tCommonAssignmentReturnValue } from './types';
import useAssignmentActions from './useAssignmentActions';
import useAssignmentTaskLicenses from './useAssignmentTaskLicenses';
import type { tAssignmentTaskLicensesReturnType } from './useAssignmentTaskLicenses';
import {
  getPersonInfo,
  canStudentAddNewAnswer,
  getLastStudentMessage,
  getLastMessage
} from './util';

type tAssignmentData = tCommonAssignmentReturnValue['assignmentData'];
type tReturnType = tCommonAssignmentReturnValue & tAssignmentTaskLicensesReturnType;
const useAssignmentTask = ({ task }: { task: IAssignmentTask }): tReturnType => {
  const { lessonId, id: taskId } = task;
  const lastAssignmentRef = useRef<tAssignmentData | null>(null);
  const { actions, documentResource } = useAssignmentActions({
    documentId: null,
    taskId,
    lessonId
  });

  const profile = useSelector(userSharedSelectors.selectProfile);

  const { licensesCorrectionInfo, canSubmit, canAcceptAssignmentTask } =
    useAssignmentTaskLicenses();
  const { currentLicense } = licensesCorrectionInfo;

  const { data: documentData, status, pendingText } = documentResource;

  const assignment = useMemo(() => {
    let normalizedData: tAssignmentNormalizedData;
    const isFinishedRequest = status === 'received' || status === 'submit_failed';

    if (isFinishedRequest && documentData === null) {
      // assignment task fetched but haven't answered yet
      normalizedData = assignmentTaskAdapter({
        assignmentTask: task,
        license: currentLicense
      });
    } else if (isFinishedRequest && documentData) {
      // assignment task fetched and already has answered document
      normalizedData = documentData;
    } else if (
      status === 'submit_loading' &&
      !documentData &&
      lastAssignmentRef.current
    ) {
      // assignment task is submitting first answer - use old assignment
      return lastAssignmentRef.current;
    } else if (/_loading/.test(status) && documentData) {
      // use old data while loading
      normalizedData = documentData;
    } else {
      return null;
    }

    const assignmentData = normalizedData
      ? {
          assignable: normalizedData.assignmentTask,
          conversation: normalizedData.conversation,
          status: normalizedData.status
        }
      : null;
    const messages =
      prepareChatMessagesForTaskAssignment({ task, assignment: assignmentData }) ?? [];
    const { answerType, minimalAnswerLength, title, avgResponseTime } =
      normalizedData.assignmentTask;
    const resultAssignment: tAssignmentData = {
      messages,
      title,
      inputType: answerType,
      minimumAnswerLength: minimalAnswerLength,
      status: normalizedData.status,
      rating: normalizedData.rating,
      estimatedReviewTime: normalizedData.estimatedReviewTime,
      assignmentId: normalizedData.assignmentId,
      owner: getPersonInfo({
        name: normalizedData.assignmentTask.author.name,
        avatarURL: normalizedData.assignmentTask.author.brand || null
      }),
      avgResponseTime: avgResponseTime || null
    };
    // store for reuse while first submit
    lastAssignmentRef.current = resultAssignment;

    return resultAssignment;
  }, [documentData, currentLicense, status, task]);

  const lastStudentMessage = getLastStudentMessage(assignment?.messages);

  const canChangeLicense = useMemo(
    () => licensesCorrectionInfo.requireLicenseSelector && documentData === null,
    [documentData, licensesCorrectionInfo.requireLicenseSelector]
  );

  if (status === 'submit_loading' && pendingText) {
    // Check if we already have a pending outgoing message
    const lastMessage = getLastMessage(assignment?.messages);
    const hasPendingOutgoing =
      lastMessage?.from === 'outgoing' && lastMessage?.state === 'loading';

    // Only add a new outgoing message if we don't already have one
    if (!hasPendingOutgoing) {
      // If editing, remove the old outgoing message first
      if (lastMessage?.from === 'outgoing') {
        assignment?.messages.pop();
      }
      // Add the student's message with the actual text they submitted
      assignment?.messages.push({
        from: 'outgoing',
        state: 'done',
        text: pendingText,
        author: {
          name: profile ? `${profile.fname} ${profile.lname}` : '',
          avatar: profile?.avatarURL || null
        },
        createdAt: new Date().toISOString(),
        key: 'outgoing-pending'
      });
    }
  }

  return {
    assignmentData: assignment,
    status,
    actions,
    licensesCorrectionInfo,
    canChangeLicense,
    canSubmit,
    canAcceptAssignmentTask,
    studentCanAddNewAnswer: canStudentAddNewAnswer(assignment),
    lastStudentMessage
  };
};

export default useAssignmentTask;
